import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { EmojiEvents, Cancel, Percent } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import crmPlusService from '../../services/crmPlusService';

/**
 * Win / Loss — closed-deal outcomes for the period: won vs lost value, why
 * deals were lost, who beat us, and a proxy for where in the pipeline deals
 * died. The stage-proxy is explicitly a probability-band approximation (the
 * backend doesn't retain true stage-at-loss), so its `note` is always shown
 * verbatim rather than presenting the bands as exact stage history.
 */

interface WinLossSummary {
  wonCount: number;
  wonValue: number;
  lostCount: number;
  lostValue: number;
  winRatePct: number | null;
}
interface LossReasonRow {
  reason: string;
  count: number;
  value: number;
}
interface CompetitorRow {
  competitor: string;
  count: number;
  value: number;
}
interface StageProxyBand {
  band: string;
  count: number;
  value: number;
}
interface WinLossResponse {
  period?: { from: string; to: string };
  summary: WinLossSummary;
  lossReasons: LossReasonRow[];
  competitors: CompetitorRow[];
  lostByStageProxy: { note: string; bands: StageProxyBand[] };
}

const isNum = (v: any): v is number => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Number(v).toFixed(1)}%` : '—');
const fmtInt = (v: any) => (isNum(v) ? String(Number(v)) : '—');

const fmtDate = (v?: string) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const WinLossPage: React.FC = () => {
  const T = useChartTokens();

  const [data, setData] = useState<WinLossResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    crmPlusService
      .getWinLoss()
      .then((res: WinLossResponse) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load win/loss data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary;
  const lossReasons = useMemo(
    () => [...(data?.lossReasons || [])].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0)),
    [data]
  );
  const competitors = useMemo(
    () => [...(data?.competitors || [])].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0)),
    [data]
  );
  const bands = data?.lostByStageProxy?.bands || [];
  const proxyNote = data?.lostByStageProxy?.note;

  const wonLostChart = useMemo(
    () =>
      summary
        ? [
            { name: 'Won', value: Number(summary.wonValue) || 0, tone: T.status.good },
            { name: 'Lost', value: Number(summary.lostValue) || 0, tone: T.status.critical },
          ]
        : [],
    [summary, T]
  );

  const hasAnyData =
    !!summary &&
    ((Number(summary.wonCount) || 0) > 0 ||
      (Number(summary.lostCount) || 0) > 0 ||
      lossReasons.length > 0 ||
      competitors.length > 0);

  return (
    <Box>
      <PageHeader
        title="Win / Loss Analysis"
        subtitle={
          data?.period
            ? `Closed deal outcomes, ${fmtDate(data.period.from)} – ${fmtDate(data.period.to)}`
            : 'Closed-won vs closed-lost outcomes, loss reasons and competitor breakdown'
        }
      />

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : !hasAnyData ? (
        <TableSurface>
          <TableEmptyState
            title="No closed deals this period"
            hint="Win/loss analysis populates once opportunities are closed won or lost."
          />
        </TableSurface>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
              mb: 2.5,
            }}
          >
            <StatCard
              title="Won"
              value={fmtMoney(summary?.wonValue)}
              icon={<EmojiEvents />}
              color={T.status.good}
              footer={
                <Typography variant="caption" color="text.secondary">
                  {fmtInt(summary?.wonCount)} deals won
                </Typography>
              }
            />
            <StatCard
              title="Lost"
              value={fmtMoney(summary?.lostValue)}
              icon={<Cancel />}
              color={T.status.critical}
              footer={
                <Typography variant="caption" color="text.secondary">
                  {fmtInt(summary?.lostCount)} deals lost
                </Typography>
              }
            />
            <StatCard
              title="Win Rate"
              value={fmtPct(summary?.winRatePct)}
              icon={<Percent />}
              color={T.series(0)}
            />
          </Box>

          {/* Won vs Lost value */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Won vs Lost Value
            </Typography>
            {wonLostChart.some((d) => d.value > 0) ? (
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={wonLostChart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid {...gridProps(T)} />
                    <XAxis dataKey="name" {...axisProps(T)} />
                    <YAxis {...axisProps(T)} tickFormatter={(v: number) => formatIndianNumber(v)} width={72} />
                    <Tooltip {...tooltipStyle(T)} formatter={(v: any) => formatIndianNumber(Number(v))} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {wonLostChart.map((d, i) => (
                        <Cell key={i} fill={d.tone} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <TableEmptyState title="No won/lost value to chart yet" />
            )}
          </Paper>

          {/* Loss reasons */}
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Loss Reasons
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Reason', 'Count', 'Value'].map((h, i) => (
                      <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lossReasons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <TableEmptyState title="No loss reasons recorded" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    lossReasons.map((r) => (
                      <TableRow key={r.reason} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.reason || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtInt(r.count)}</TableCell>
                        <TableCell align="right">{fmtMoney(r.value)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>

          {/* Competitor breakdown */}
          <Box sx={{ mt: 2.5 }}>
            <TableSurface>
              <Box sx={{ p: 2.5, pb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Competitor Breakdown
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['Competitor', 'Count', 'Value'].map((h, i) => (
                        <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={headerCellSx}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {competitors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ border: 0 }}>
                          <TableEmptyState title="No competitor data recorded" />
                        </TableCell>
                      </TableRow>
                    ) : (
                      competitors.map((c) => (
                        <TableRow key={c.competitor} sx={bodyRowSx}>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.competitor || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtInt(c.count)}</TableCell>
                          <TableCell align="right">{fmtMoney(c.value)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Box>

          {/* Stage proxy bands */}
          <Box sx={{ mt: 2.5 }}>
            <TableSurface>
              <Box sx={{ p: 2.5, pb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Lost-by-Stage (Proxy)
                </Typography>
                {proxyNote && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {proxyNote}
                  </Typography>
                )}
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['Band', 'Count', 'Value'].map((h, i) => (
                        <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={headerCellSx}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bands.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ border: 0 }}>
                          <TableEmptyState title="No stage-proxy data available" />
                        </TableCell>
                      </TableRow>
                    ) : (
                      bands.map((b) => (
                        <TableRow key={b.band} sx={bodyRowSx}>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {b.band || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtInt(b.count)}</TableCell>
                          <TableCell align="right">{fmtMoney(b.value)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WinLossPage;
